import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomConfirmationModal from './CustomConfirmationModal';
import { useColorTheme } from '../../context/ColorThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIFeedbackSimulation = ({ campaign, sessionToken, onClose, compact = false }) => {
  const { colorTheme } = useColorTheme();
  // Early validation to prevent blank screens
  if (!campaign || !sessionToken) {
    console.warn('AI Feedback Modal: Missing required props', { campaign: !!campaign, sessionToken: !!sessionToken });
    if (onClose) onClose();
    return null;
  }
  const [isGenerating, setIsGeneratingLocal] = useState(false); // Local state for AI feedback generation
  const [feedback, setFeedback] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [generationInfo, setGenerationInfo] = useState(null);
  const [consolidatedSuggestions, setConsolidatedSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('facebook');
  const [feedbackCount, setFeedbackCount] = useState(5);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationProgress, setRegenerationProgress] = useState({
    actualProgress: 0,
    message: '',
    startTime: null
  });
  const [showRegenerationPreview, setShowRegenerationPreview] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccessCelebration, setShowSuccessCelebration] = useState(false);
  const [regeneratedCampaignId, setRegeneratedCampaignId] = useState(null);
  
  // Editing states for Enhanced Description and AI Improvements
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingImprovements, setIsEditingImprovements] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedImprovements, setEditedImprovements] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Existing feedback states
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [existingFeedbackError, setExistingFeedbackError] = useState(null);
  
  // Credit calculation state for regeneration
  const [creditInfo, setCreditInfo] = useState({
    totalCredits: 0,
    breakdown: [],
    userBalance: 0,
    balanceAfter: 0
  });

  // Calculate credits needed for regeneration based on ad types
  const calculateRegenerationCredits = () => {
    if (!campaign?.generated_ads) return { totalCredits: 0, breakdown: [] };
    
    // Credit costs per ad type
    const creditCosts = {
      text_ad: 3,
      social_media: 3,
      google_ads: 3,
      email_marketing: 3,
      product_description: 3,
      whatsapp_ad: 3,
      image_promo: 15,
      video_promo: 50,
      video_ad: 50
    };
    
    // Count original ads (exclude versions)
    const adCounts = {};
    campaign.generated_ads.forEach(ad => {
      const version = ad.version || 1;
      if (version <= 1) {
        const adType = ad.ad_type || 'text_ad';
        adCounts[adType] = (adCounts[adType] || 0) + 1;
      }
    });
    
    // Calculate breakdown
    const breakdown = [];
    let totalCredits = 0;
    
    Object.entries(adCounts).forEach(([adType, count]) => {
      const costPerAd = creditCosts[adType] || 3;
      const subtotal = count * costPerAd;
      totalCredits += subtotal;
      
      const adTypeLabel = adType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      breakdown.push({
        type: adTypeLabel,
        count,
        costPerAd,
        subtotal
      });
    });
    
    return { totalCredits, breakdown };
  };

  // Fetch user credit balance
  const fetchUserBalance = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/user/balance`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      return response.data.credits || 0;
    } catch (err) {
      console.error('Failed to fetch user balance:', err);
      return 0;
    }
  };

  const generateAIFeedback = async () => {
    if (!campaign?.id) {
      setError('Campaign not found');
      return;
    }

    setIsGeneratingLocal(true);
    setError(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/campaigns/${campaign.id}/ai-feedback`,
        {
          platform: selectedPlatform,
          feedback_count: feedbackCount,
          model: 'groq'
        },
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setFeedback(response.data.feedback);
      setStatistics(response.data.statistics);
      setGenerationInfo(response.data.generation_info);
      setConsolidatedSuggestions(response.data.consolidated_suggestions || []);
    } catch (err) {
      console.error('AI Feedback generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate AI feedback');
    } finally {
      setIsGeneratingLocal(false);
    }
  };

  const saveFeedback = async () => {
    if (!campaign?.id || !feedback.length) {
      setError('No feedback to save');
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/campaigns/${campaign.id}/ai-feedback/save`,
        {
          feedback,
          statistics,
          consolidated_suggestions: consolidatedSuggestions,
          generation_info: generationInfo,
          notes: `AI feedback generated on ${new Date().toLocaleString()}`
        },
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setIsSaved(true);
      console.log('Feedback saved successfully:', response.data);
      
      // Refresh existing feedback display to show the newly saved feedback
      loadExistingFeedback();
      
      // Immediately trigger UI update for saved feedback indicators
      if (window.refreshSavedFeedbackIndicators) {
        window.refreshSavedFeedbackIndicators();
      }
    } catch (err) {
      console.error('Error saving feedback:', err);
      setError(err.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setIsSaving(false);
    }
  };

  const regenerateWithFeedback = async () => {
    if (!campaign?.id || !isSaved) {
      setError('Please save feedback first before regenerating');
      return;
    }

    // Calculate credits and fetch user balance
    const { totalCredits, breakdown } = calculateRegenerationCredits();
    const userBalance = await fetchUserBalance();
    
    setCreditInfo({
      totalCredits,
      breakdown,
      userBalance,
      balanceAfter: userBalance - totalCredits
    });

    // Show custom confirmation modal with credit info
    setShowConfirmation(true);
  };

  const handleConfirmRegeneration = async () => {
    setShowConfirmation(false);
    setIsRegenerating(true);
    
    // Initialize progress tracking
    setRegenerationProgress({
      actualProgress: 0,
      message: '🚀 Initializing campaign regeneration with AI improvements...',
      startTime: Date.now()
    });
    
    try {
      // ALWAYS reset flag at the START of regeneration
      window.userChoseBackgroundMode = false;
      console.log('🔄 AI Feedback Regenerate: Reset userChoseBackgroundMode to false');
      
      // Prepare regeneration data with edited values
      const regenerationData = {
        enhanced_description: editedDescription || '',
        ai_improvements: editedImprovements ? editedImprovements.split('\n').filter(imp => imp.trim()) : [],
        original_suggestions: []  // Backend gets suggestions from saved feedback, not from request
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/campaigns/${campaign.id}/regenerate-with-feedback`,
        regenerationData,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Campaign regenerated with feedback - starting polling:', response.data);
      
      // ===============================================
      // 🚀 START POLLING FOR AI FEEDBACK REGENERATION
      // Backend creates stub and processes in background
      // ===============================================
      const campaignId = response.data.id || campaign.id;
      console.log('🔄 AI Feedback Regenerate: Starting campaign polling for:', campaignId);
      
      // Store campaign ID for polling
      window.currentPollingCampaignId = campaignId;
      
      // Start polling for campaign status
      const pollCampaign = async () => {
        try {
          const statusResponse = await axios.get(
            `${BACKEND_URL}/api/campaigns/${campaignId}/status`,
            { headers: { Authorization: `Bearer ${sessionToken}` } }
          );
          
          const { status, progress } = statusResponse.data;
          console.log(`📊 AI Feedback Regenerate: Campaign ${campaignId} polling: status=${status}, progress=${progress}%`);
          
          // Update progress UI
          setRegenerationProgress(prev => ({
            ...prev,
            actualProgress: progress,
            message: status === 'generating' 
              ? `🎨 Regenerating campaign with AI improvements... ${progress}% complete`
              : status === 'completed'
              ? '✅ Campaign regeneration complete!'
              : status === 'queued'
              ? '⏳ Campaign queued for regeneration...'
              : status === 'failed'
              ? '❌ Campaign regeneration failed'
              : 'Processing...'
          }));
          
          // If completed, show success and close
          if (status === 'completed') {
            console.log('✅ AI Feedback Regenerate: Campaign status is COMPLETED');
            
            // Add a small delay to ensure backend has fully processed
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Store the campaign ID and show success celebration
            setRegeneratedCampaignId(campaignId);
            setIsRegenerating(false);
            setShowSuccessCelebration(true);
            
            // Trigger refresh of dashboard campaigns
            if (window.refreshSavedFeedbackIndicators) {
              window.refreshSavedFeedbackIndicators();
            }
            
            // Clear polling campaign ID
            window.currentPollingCampaignId = null;
            return true; // Stop polling
          } else if (status === 'failed') {
            console.error('❌ AI Feedback Regenerate: Campaign regeneration failed');
            setError('Campaign regeneration failed. Please try again.');
            window.currentPollingCampaignId = null;
            setIsRegenerating(false);
            return true; // Stop polling
          }
          
          return false; // Continue polling
        } catch (pollError) {
          console.error('Error polling campaign status:', pollError);
          return false;
        }
      };
      
      // Poll every 2 seconds
      const pollInterval = setInterval(async () => {
        const shouldStop = await pollCampaign();
        if (shouldStop) {
          clearInterval(pollInterval);
        }
      }, 2000);
      
      // Initial poll
      const shouldStop = await pollCampaign();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
      
      // Don't execute the code below - polling handles everything
      return;
      
    } catch (err) {
      console.error('Error regenerating campaign:', err);
      const errorMessage = err.response?.status === 503 
        ? 'Service temporarily unavailable. The backend service may be restarting. Please try again in a moment.'
        : err.response?.data?.detail || 'Failed to regenerate campaign with feedback. Please try again.';
      
      setError(errorMessage);
      setIsRegenerating(false);
      setRegenerationProgress({
        actualProgress: 0,
        message: '',
        startTime: null
      });
      
      // Show error alert
      alert(`❌ Regeneration Failed\n\n${errorMessage}`);
    }
  };

  const showRegenerationDetails = () => {
    if (!consolidatedSuggestions.length) {
      setError('No improvement suggestions available');
      return;
    }

    // Initialize editable fields with generated content
    const enhancedContent = generateEnhancedContent();
    if (enhancedContent) {
      setEditedDescription(enhancedContent.enhanced.description);
      // consolidatedSuggestions is an array of strings, not objects
      setEditedImprovements(consolidatedSuggestions.join('\n'));
    }
    
    // Calculate credits for regeneration preview
    const { totalCredits, breakdown } = calculateRegenerationCredits();
    fetchUserBalance().then(userBalance => {
      setCreditInfo({
        totalCredits,
        breakdown,
        userBalance,
        balanceAfter: userBalance - totalCredits
      });
    });
    
    setShowRegenerationPreview(true);
    setHasUnsavedChanges(false);
  };

  // Handle editing functions
  const handleDescriptionEdit = () => {
    setIsEditingDescription(true);
  };

  const handleImprovementsEdit = () => {
    setIsEditingImprovements(true);
  };

  const saveDescriptionEdit = () => {
    setIsEditingDescription(false);
    setHasUnsavedChanges(true);
  };

  const saveImprovementsEdit = () => {
    setIsEditingImprovements(false);
    setHasUnsavedChanges(true);
  };

  const cancelDescriptionEdit = () => {
    const enhancedContent = generateEnhancedContent();
    if (enhancedContent) {
      setEditedDescription(enhancedContent.enhanced.description);
    }
    setIsEditingDescription(false);
  };

  const cancelImprovementsEdit = () => {
    // Reset to original consolidated suggestions
    setEditedImprovements(consolidatedSuggestions.join('\n'));
    setIsEditingImprovements(false);
  };

  // Load existing AI feedback for this campaign (same endpoint as View Details screen)
  const loadExistingFeedback = async () => {
    setIsLoadingExisting(true);
    setExistingFeedbackError(null);
    
    try {
      console.log('Loading existing AI feedback for campaign:', campaign.id);
      
      // Use the same endpoint as View Details screen for consistency
      const response = await axios.get(
        `${BACKEND_URL}/api/campaigns/${campaign.id}/feedback/combined`,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.ai_feedback && response.data.ai_feedback.has_saved_feedback) {
        // Transform combined stats data to match existing feedback format
        const combinedData = response.data;
        const transformedFeedback = {
          feedback_data: {
            statistics: {
              total_feedback: combinedData.ai_feedback.total_feedback || 0,
              average_rating: combinedData.ai_feedback.average_rating || 0,
              sentiment_distribution: combinedData.ai_feedback.sentiment_distribution || { positive: 0, neutral: 0, negative: 0 }
            },
            consolidated_suggestions: combinedData.ai_feedback.consolidated_suggestions || [],
            // Extract individual AI reviews from the combined data
            generated_feedback: combinedData.ai_feedback.feedback_entries || combinedData.ai_feedback.generated_feedback || []
          },
          created_at: combinedData.ai_feedback.last_generated || combinedData.ai_feedback.created_at || new Date().toISOString(),
          campaign_id: campaign.id
        };
        
        setExistingFeedback(transformedFeedback);
        console.log('Loaded existing AI feedback from combined stats:', transformedFeedback);
        console.log('DEBUG: Consolidated suggestions:', transformedFeedback.feedback_data.consolidated_suggestions);
        console.log('DEBUG: Generated feedback:', transformedFeedback.feedback_data.generated_feedback);
        console.log('DEBUG: Raw combined data structure:', combinedData);
      } else {
        console.log('No existing AI feedback found in combined stats for this campaign');
        setExistingFeedback(null);
      }
    } catch (error) {
      console.log('No existing AI feedback or error loading combined stats:', error.response?.status);
      if (error.response?.status === 404) {
        // 404 is expected if no feedback exists yet
        setExistingFeedback(null);
      } else {
        setExistingFeedbackError('Failed to load existing feedback');
      }
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Load existing feedback when component mounts
  useEffect(() => {
    if (campaign && sessionToken) {
      loadExistingFeedback();
    }
  }, [campaign?.id, sessionToken]);

  const generateEnhancedContent = () => {
    if (!campaign || !consolidatedSuggestions.length) return null;

    const originalProduct = campaign.product || {};
    
    // Create a cleaner, more engaging enhanced description
    const topSuggestions = consolidatedSuggestions.slice(0, 3);
    const enhancedDescription = `${originalProduct.product_description || ''}

🚀 Enhanced with AI-powered improvements for better engagement and conversion.`;

    return {
      original: {
        name: originalProduct.product_name || 'Untitled Product',
        description: originalProduct.product_description || 'No description available',
        benefits: originalProduct.key_benefits || 'No benefits listed',
        voice: originalProduct.brand_voice || 'professional',
        demographics: {
          age_range: `${originalProduct.age_range_min || 18}-${originalProduct.age_range_max || 65} years`,
          gender: originalProduct.gender || 'All genders',
          geography: originalProduct.geography || 'Not specified',
          interests: originalProduct.interests_behaviors || 'General audience'
        }
      },
      enhanced: {
        name: originalProduct.product_name || 'Untitled Product', 
        description: enhancedDescription,
        benefits: originalProduct.key_benefits || 'No benefits listed',
        voice: originalProduct.brand_voice || 'professional',
        demographics: {
          age_range: `${originalProduct.age_range_min || 18}-${originalProduct.age_range_max || 65} years`,
          gender: originalProduct.gender || 'All genders', 
          geography: originalProduct.geography || 'Not specified',
          interests: originalProduct.interests_behaviors || 'General audience'
        },
        improvements: topSuggestions
      }
    };
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'enthusiast': return '😍';
      case 'skeptical': return '🤔';
      case 'expert': return '🎓';
      case 'critic': return '😐';
      default: return '👤';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < Math.floor(rating) ? 'text-green-500' : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  if (compact) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            🤖 AI Feedback Preview
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
              Beta
            </span>
          </h3>
          <div className="flex items-center space-x-2">
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={isGenerating}
            >
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="general">General</option>
            </select>
            <button
              onClick={generateAIFeedback}
              disabled={isGenerating}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm px-4 py-2 rounded-lg transition-colors flex items-center"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  ✨ Generate Preview
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {statistics && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{statistics.average_rating}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Avg Rating</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{statistics.total_feedback}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Reviews</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {Math.round((statistics.sentiment_distribution?.positive || 0) / statistics.total_feedback * 100) || 0}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Positive</div>
              </div>
            </div>
          </div>
        )}

        {feedback.length > 0 && (
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {feedback.slice(0, 3).map((item, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getUserTypeIcon(item.user_type)}</span>
                    <div className="flex">{renderStars(item.rating)}</div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getSentimentColor(item.sentiment)}`}>
                      {item.sentiment}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 capitalize">{item.platform}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{item.comment}</p>
              </div>
            ))}
            {feedback.length > 3 && (
              <div className="text-center text-sm text-gray-500">
                +{feedback.length - 3} more reviews
              </div>
            )}
            
            {/* Consolidated Suggestions for Compact Mode */}
            {consolidatedSuggestions.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 rounded">
                <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  💡 Key Improvements:
                </h5>
                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  {consolidatedSuggestions.slice(0, 2).map((suggestion, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                  {consolidatedSuggestions.length > 2 && (
                    <li className="text-xs text-gray-500">+{consolidatedSuggestions.length - 2} more suggestions</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            🤖 AI Feedback Simulation
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Get instant feedback on your campaign from AI-generated social media users. This helps you understand potential reception before publishing.
            </p>
            
            <div className="flex items-center space-x-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Platform
                </label>
                <select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  disabled={isGenerating}
                >
                  <option value="facebook">Facebook Users</option>
                  <option value="instagram">Instagram Users</option>
                  <option value="general">General Audience</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Feedback Count
                </label>
                <select
                  value={feedbackCount}
                  onChange={(e) => setFeedbackCount(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  disabled={isGenerating}
                >
                  <option value={3}>3 Reviews</option>
                  <option value={5}>5 Reviews</option>
                  <option value={8}>8 Reviews</option>
                  <option value={10}>10 Reviews</option>
                </select>
              </div>
              
              <div className="flex-1 flex justify-end items-end">
                <button
                  onClick={generateAIFeedback}
                  disabled={isGenerating}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors flex items-center btn-ripple"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Generating Feedback...
                    </>
                  ) : (
                    <>
                      ✨ Generate AI Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Existing AI Feedback Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              📋 Existing AI Feedback
              {isLoadingExisting && (
                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              )}
            </h3>
            
            {isLoadingExisting ? (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="animate-pulse">
                  <div className="text-gray-500 dark:text-gray-400">Loading existing feedback...</div>
                </div>
              </div>
            ) : existingFeedbackError ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <div className="text-yellow-800 dark:text-yellow-200">
                  ⚠️ {existingFeedbackError}
                </div>
              </div>
            ) : existingFeedback ? (
              <div className={`rounded-lg p-4 border ${colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700' : colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`font-medium ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>
                    ✅ AI feedback available for this campaign
                  </div>
                  <div className={`text-xs ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                    Generated: {new Date(existingFeedback.created_at).toLocaleDateString()} at {new Date(existingFeedback.created_at).toLocaleTimeString()}
                  </div>
                </div>
                
                {existingFeedback.feedback_data.statistics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600 dark:text-green-400">
                        {existingFeedback.feedback_data.statistics.total_feedback || 0}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Reviews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                        {existingFeedback.feedback_data.statistics.average_rating || 0}/5
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Avg Rating</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {Math.round((existingFeedback.feedback_data.statistics.sentiment_distribution?.positive || 0) / existingFeedback.feedback_data.statistics.total_feedback * 100) || 0}%
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Positive</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {existingFeedback.feedback_data.consolidated_suggestions?.length || 0}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Suggestions</div>
                    </div>
                  </div>
                )}
                
                <div className="text-sm text-green-700 dark:text-green-300">
                  💡 You already have AI feedback for this campaign. You can generate new feedback to get fresh insights or use the existing data.
                </div>
                
                {existingFeedback.feedback_data.consolidated_suggestions && existingFeedback.feedback_data.consolidated_suggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                    <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">Recent AI Suggestions:</div>
                    <div className="text-xs text-green-700 dark:text-green-300 space-y-1">
                      {existingFeedback.feedback_data.consolidated_suggestions.slice(0, 3).map((suggestion, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="mr-1 text-green-500">•</span>
                          <span>{suggestion}</span>
                        </div>
                      ))}
                      {existingFeedback.feedback_data.consolidated_suggestions.length > 3 && (
                        <div className="text-green-600 dark:text-green-400">
                          ...and {existingFeedback.feedback_data.consolidated_suggestions.length - 3} more suggestions
                        </div>
                      )}
                    </div>
                  </div>
                )}
              
              {/* Detailed AI Improvement Recommendations Section */}
              {existingFeedback && existingFeedback.feedback_data.consolidated_suggestions && existingFeedback.feedback_data.consolidated_suggestions.length > 0 && (
                <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                    💡 AI Improvement Recommendations
                    <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full">
                      {existingFeedback.feedback_data.consolidated_suggestions.length} suggestions
                    </span>
                  </h4>
                  <div className="space-y-2">
                    {existingFeedback.feedback_data.consolidated_suggestions && existingFeedback.feedback_data.consolidated_suggestions.length > 0 ? (
                      existingFeedback.feedback_data.consolidated_suggestions.map((suggestion, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <span className="mr-2 text-blue-500 font-bold text-base">{idx + 1}.</span>
                          <span className="text-blue-700 dark:text-blue-300 leading-relaxed">{suggestion}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-blue-600 dark:text-blue-400 text-sm">
                        DEBUG: No consolidated suggestions found in data structure
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Individual AI Reviews Section */}
              {existingFeedback && existingFeedback.feedback_data.generated_feedback && existingFeedback.feedback_data.generated_feedback.length > 0 && (
                <div className="mt-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-purple-800 dark:text-purple-200 mb-3 flex items-center">
                    📝 Individual AI Reviews
                    <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300 px-2 py-1 rounded-full">
                      {existingFeedback.feedback_data.generated_feedback.length} reviews
                    </span>
                  </h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {existingFeedback.feedback_data.generated_feedback && existingFeedback.feedback_data.generated_feedback.length > 0 ? (
                      existingFeedback.feedback_data.generated_feedback.slice(0, 5).map((review, idx) => (
                      <div key={idx} className="bg-white dark:bg-purple-900/40 p-3 rounded border border-purple-200 dark:border-purple-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, starIdx) => (
                                <span
                                  key={starIdx}
                                  className={`text-sm ${
                                    starIdx < (review.rating || 0)
                                      ? 'text-green-500'
                                      : 'text-gray-300 dark:text-gray-600'
                                  }`}
                                >
                                  ⭐
                                </span>
                              ))}
                              <span className="ml-1 text-sm font-semibold text-purple-700 dark:text-purple-300">
                                {review.rating || 0}/5
                              </span>
                            </div>
                            {review.user_type && (
                              <span className="text-xs bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300 px-2 py-1 rounded-full">
                                {review.user_type}
                              </span>
                            )}
                          </div>
                          {review.sentiment && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              review.sentiment === 'positive' 
                                ? 'bg-green-100 text-green-600 dark:bg-green-800 dark:text-green-300' 
                                : review.sentiment === 'negative'
                                ? 'bg-red-100 text-red-600 dark:bg-red-800 dark:text-red-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {review.sentiment}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-purple-700 dark:text-purple-300 leading-relaxed">
                          "{review.comment || 'No comment provided'}"
                        </p>
                        {review.platform && (
                          <div className="mt-2 text-xs text-purple-500 dark:text-purple-400">
                            Platform: {review.platform}
                          </div>
                        )}
                      </div>
                      ))
                    ) : (
                      <div className="text-purple-600 dark:text-purple-400 text-sm">
                        DEBUG: No generated feedback found in data structure
                      </div>
                    )}
                    {existingFeedback.feedback_data.generated_feedback && existingFeedback.feedback_data.generated_feedback.length > 5 && (
                      <div className="text-center py-2">
                        <span className="text-sm text-purple-600 dark:text-purple-400">
                          ...and {existingFeedback.feedback_data.generated_feedback.length - 5} more reviews
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="text-blue-800 dark:text-blue-200">
                  🆕 No existing AI feedback found for this campaign. Generate new feedback to get insights.
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <strong>Error:</strong> {error}
            </div>
          )}

          {statistics && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                📊 Feedback Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{statistics.average_rating}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Average Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{statistics.total_feedback}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Total Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {Math.round((statistics.sentiment_distribution?.positive || 0) / statistics.total_feedback * 100) || 0}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Positive</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {Math.round((statistics.sentiment_distribution?.negative || 0) / statistics.total_feedback * 100) || 0}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Needs Work</div>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating Distribution</h4>
                <div className="flex items-center space-x-4">
                  {[5, 4, 3, 2, 1].map(rating => (
                    <div key={rating} className="flex items-center space-x-1">
                      <span className="text-sm">{rating}⭐</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            colorTheme === 'purple' 
                              ? 'bg-purple-500' 
                              : colorTheme === 'blue' 
                              ? 'bg-blue-500' 
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${((statistics.rating_distribution?.[rating] || 0) / statistics.total_feedback) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {statistics.rating_distribution?.[rating] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Consolidated Suggestions Section */}
          {consolidatedSuggestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💡 Key Improvement Recommendations
              </h3>
              <div className={`border-l-4 p-6 rounded-lg ${
                colorTheme === 'purple' 
                  ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-400' 
                  : colorTheme === 'blue' 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-400' 
                  : 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-400'
              }`}>
                <ul className="space-y-3">
                  {consolidatedSuggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start text-blue-900 dark:text-blue-200">
                      <span className="mr-3 text-blue-600 font-bold">{idx + 1}.</span>
                      <span className="leading-relaxed">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center space-x-3 mt-4 flex-wrap">
                <button
                  onClick={saveFeedback}
                  disabled={isSaving || isSaved}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center ${
                    isSaved 
                      ? 'bg-green-100 text-green-800 border border-green-300' 
                      : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : isSaved ? (
                    <>
                      ✅ Feedback Saved
                    </>
                  ) : (
                    <>
                      💾 Save Feedback
                    </>
                  )}
                </button>
                
                <button
                  onClick={showRegenerationDetails}
                  disabled={!isSaved}
                  className={`px-6 py-2 ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center btn-ripple`}
                  title={!isSaved ? "Save feedback first to preview changes" : "Preview what changes will be made"}
                >
                  🔍 Preview Changes
                </button>
                
                <button
                  onClick={regenerateWithFeedback}
                  disabled={!isSaved || isRegenerating}
                  className={`px-6 py-2 ${colorTheme === 'purple' ? 'bg-pink-600 hover:bg-pink-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center btn-ripple`}
                  title={!isSaved ? "Save feedback first to enable regeneration" : "Regenerate campaign with improvements"}
                >
                  {isRegenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Regenerating Campaign...
                    </>
                  ) : (
                    <>
                      🔄 Regenerate with Improvements
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {feedback.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💬 AI-Generated Feedback ({feedback.length} reviews)
              </h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {feedback.map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getUserTypeIcon(item.user_type)}</span>
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="flex">{renderStars(item.rating)}</div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              ({item.rating}/5)
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${getSentimentColor(item.sentiment)}`}>
                              {item.sentiment}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">
                              {item.user_type} • {item.platform}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        AI Generated
                      </span>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {item.comment}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {generationInfo && (
            <div className="mt-6 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded p-3">
              <div className="flex items-center justify-between">
                <span>Generated using {generationInfo.model_used} • {generationInfo.platform} users</span>
                <span>{new Date(generationInfo.generated_at).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Regeneration Preview Modal */}
        {showRegenerationPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  🔍 Campaign Regeneration Preview
                </h3>
                <button
                  onClick={() => setShowRegenerationPreview(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    📊 Current Campaign Performance
                  </h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-2xl font-bold text-orange-600">
                          {statistics?.average_rating || 0}/5
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Current Rating</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-red-600">
                          {Math.round(((statistics?.sentiment_distribution?.negative || 0) + (statistics?.sentiment_distribution?.neutral || 0)) / (statistics?.total_feedback || 1) * 100)}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Needs Improvement</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campaign Details & Content Comparison */}
                {(() => {
                  const contentComparison = generateEnhancedContent();
                  return contentComparison && (
                    <div className="mb-6">
                      {/* Campaign Details Section */}
                      <div className="mb-6">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                          👥 Your Campaign Details
                        </h4>
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-600 rounded-lg p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <div className="font-medium text-blue-800 dark:text-blue-300">Age Range</div>
                              <div className="text-blue-700 dark:text-blue-200">{contentComparison.original.demographics.age_range}</div>
                            </div>
                            <div>
                              <div className="font-medium text-blue-800 dark:text-blue-300">Gender</div>
                              <div className="text-blue-700 dark:text-blue-200">{contentComparison.original.demographics.gender}</div>
                            </div>
                            <div>
                              <div className="font-medium text-blue-800 dark:text-blue-300">Location</div>
                              <div className="text-blue-700 dark:text-blue-200">{contentComparison.original.demographics.geography}</div>
                            </div>
                            <div>
                              <div className="font-medium text-blue-800 dark:text-blue-300">Brand Voice</div>
                              <div className="text-blue-700 dark:text-blue-200 capitalize">{contentComparison.original.voice}</div>
                            </div>
                          </div>
                          {contentComparison.original.demographics.interests !== 'General audience' && (
                            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-600">
                              <div className="font-medium text-blue-800 dark:text-blue-300 text-sm">Target Interests</div>
                              <div className="text-blue-700 dark:text-blue-200 text-sm">{contentComparison.original.demographics.interests}</div>
                            </div>
                          )}
                        </div>
                      </div>

                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        📝 Content Enhancement Preview
                      </h4>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Original Content */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
                            📄 Current Campaign
                          </h5>
                          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-4">
                            <div>
                              <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Product Name</div>
                              <div className="text-gray-800 dark:text-gray-200 font-medium">{contentComparison.original.name}</div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Description</div>
                              <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed bg-white dark:bg-gray-600 p-3 rounded border">
                                {contentComparison.original.description}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Key Benefits</div>
                              <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                                {contentComparison.original.benefits}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Enhanced Content */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-green-700 dark:text-green-300 flex items-center">
                            ✨ AI-Enhanced Campaign
                          </h5>
                          <div className={`rounded-lg p-4 space-y-4 border ${colorTheme === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-600' : colorTheme === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-600'}`}>
                            <div>
                              <div className={`text-sm font-semibold mb-1 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>Product Name</div>
                              <div className={`font-medium ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>{contentComparison.enhanced.name}</div>
                            </div>
                            <div>
                              <div className={`text-sm font-semibold mb-1 flex items-center justify-between ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                                Enhanced Description
                                {!isEditingDescription && (
                                  <button
                                    onClick={handleDescriptionEdit}
                                    className="text-xs bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white px-2 py-1 rounded flex items-center btn-ripple"
                                    title="Edit Enhanced Description"
                                  >
                                    ✏️ Edit
                                  </button>
                                )}
                              </div>
                              {isEditingDescription ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className={`w-full h-24 text-sm p-3 border rounded resize-none focus:ring-2 focus:border-transparent ${colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 focus:ring-purple-500' : colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 focus:ring-blue-500' : 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 focus:ring-green-500'}`}
                                    placeholder="Enter enhanced description..."
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={saveDescriptionEdit}
                                      className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded btn-ripple`}
                                    >
                                      ✅ Save
                                    </button>
                                    <button
                                      onClick={cancelDescriptionEdit}
                                      className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                                    >
                                      ❌ Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className={`text-sm leading-relaxed p-3 rounded border ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200 bg-purple-100 dark:bg-purple-800/30' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200 bg-blue-100 dark:bg-blue-800/30' : 'text-green-800 dark:text-green-200 bg-green-100 dark:bg-green-800/30'}`}>
                                  {editedDescription || contentComparison.enhanced.description}
                                  {hasUnsavedChanges && (
                                    <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 flex items-center">
                                      ⚠️ Unsaved changes - will be applied during regeneration
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className={`text-sm font-semibold mb-1 ${colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>Key Benefits</div>
                              <div className={`text-sm leading-relaxed ${colorTheme === 'purple' ? 'text-purple-800 dark:text-purple-200' : colorTheme === 'blue' ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>
                                {contentComparison.enhanced.benefits}
                              </div>
                            </div>
                            {/* Editable AI Improvements */}
                            <div className={`p-3 rounded border ${colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-800/40 border-purple-300 dark:border-purple-600' : colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-800/40 border-blue-300 dark:border-blue-600' : 'bg-green-100 dark:bg-green-800/40 border-green-300 dark:border-green-600'}`}>
                              <div className={`text-sm font-semibold mb-2 flex items-center justify-between ${colorTheme === 'purple' ? 'text-purple-700 dark:text-purple-300' : colorTheme === 'blue' ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300'}`}>
                                🤖 AI Improvements Applied:
                                {!isEditingImprovements && (
                                  <button
                                    onClick={handleImprovementsEdit}
                                    className="text-xs bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white px-2 py-1 rounded flex items-center btn-ripple"
                                    title="Edit AI Improvements"
                                  >
                                    ✏️ Edit
                                  </button>
                                )}
                              </div>
                              {isEditingImprovements ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editedImprovements}
                                    onChange={(e) => setEditedImprovements(e.target.value)}
                                    className={`w-full h-24 text-xs p-2 border rounded resize-none focus:ring-2 focus:border-transparent ${colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-200 focus:ring-purple-500' : colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 focus:ring-blue-500' : 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-200 focus:ring-green-500'}`}
                                    placeholder="Enter AI improvements (one per line)..."
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={saveImprovementsEdit}
                                      className={`text-xs ${colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded btn-ripple`}
                                    >
                                      ✅ Save
                                    </button>
                                    <button
                                      onClick={cancelImprovementsEdit}
                                      className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                                    >
                                      ❌ Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {editedImprovements ? (
                                    editedImprovements.split('\n').filter(imp => imp.trim()).map((improvement, idx) => (
                                      <div key={idx} className="flex items-start text-xs text-green-700 dark:text-green-200">
                                        <span className="mr-1 text-green-500">•</span>
                                        <span>{improvement.trim()}</span>
                                      </div>
                                    ))
                                  ) : (
                                    consolidatedSuggestions.map((improvement, idx) => (
                                      <div key={idx} className="flex items-start text-xs text-green-700 dark:text-green-200">
                                        <span className="mr-1 text-green-500">•</span>
                                        <span>{improvement}</span>
                                      </div>
                                    ))
                                  )}
                                  {hasUnsavedChanges && (
                                    <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 flex items-center">
                                      ⚠️ Unsaved changes - will be applied during regeneration
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    🚀 Improvement Summary
                  </h4>
                  <div className="space-y-3">
                    {consolidatedSuggestions.map((suggestion, idx) => (
                      <div key={idx} className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded">
                        <div className="flex items-start">
                          <span className="text-blue-600 font-bold mr-3">{idx + 1}.</span>
                          <div>
                            <p className="text-blue-900 dark:text-blue-200 font-medium mb-2">
                              {suggestion}
                            </p>
                            <p className="text-blue-700 dark:text-blue-300 text-sm">
                              ✅ This improvement will be automatically incorporated into your regenerated campaign
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded mb-6">
                  <h5 className="text-yellow-800 dark:text-yellow-300 font-medium mb-2">
                    ⚠️ Important Notes:
                  </h5>
                  <ul className="text-yellow-700 dark:text-yellow-300 text-sm space-y-1">
                    <li>• Your original campaign will remain unchanged</li>
                    <li>• A new improved campaign will be created</li>
                    <li>• All {consolidatedSuggestions.length} AI suggestions will be incorporated</li>
                    <li>• You can compare both versions in your dashboard</li>
                  </ul>
                </div>

                {/* Credit Cost Summary */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-600 rounded-lg p-4 mb-6">
                  <h5 className="text-blue-800 dark:text-blue-300 font-medium mb-3 flex items-center">
                    💳 Credit Cost for Regeneration
                  </h5>
                  <div className="space-y-2 text-sm">
                    {creditInfo.breakdown.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-blue-700 dark:text-blue-300">
                        <span>{item.type} ({item.count}x)</span>
                        <span>{item.subtotal} credits</span>
                      </div>
                    ))}
                    <div className="border-t border-blue-300 dark:border-blue-500 pt-2 mt-2 flex justify-between font-semibold text-blue-900 dark:text-blue-200">
                      <span>Total Cost:</span>
                      <span>{creditInfo.totalCredits} credits</span>
                    </div>
                    <div className="flex justify-between text-blue-700 dark:text-blue-300">
                      <span>Your Balance:</span>
                      <span>{creditInfo.userBalance} credits</span>
                    </div>
                    <div className={`flex justify-between font-semibold ${creditInfo.balanceAfter >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      <span>After Regeneration:</span>
                      <span>{creditInfo.balanceAfter} credits</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setShowRegenerationPreview(false)}
                    className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowRegenerationPreview(false);
                      regenerateWithFeedback();
                    }}
                    disabled={isRegenerating || creditInfo.balanceAfter < 0}
                    className={`px-6 py-2 ${creditInfo.balanceAfter < 0 ? 'bg-gray-400 cursor-not-allowed' : colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center btn-ripple`}
                  >
                    {isRegenerating ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Regenerating...
                      </>
                    ) : creditInfo.balanceAfter < 0 ? (
                      <>❌ Insufficient Credits</>
                    ) : (
                      <>🚀 Regenerate ({creditInfo.totalCredits} credits)</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regeneration Progress Modal */}
        {isRegenerating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center">
                <div className={`animate-spin rounded-full h-16 w-16 border-4 mx-auto mb-6 ${
                  colorTheme === 'purple' 
                    ? 'border-purple-200 border-t-purple-600' 
                    : colorTheme === 'blue' 
                    ? 'border-blue-200 border-t-blue-600' 
                    : 'border-green-200 border-t-green-600'
                }`}></div>
                
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  🔄 Regenerating Campaign with AI Improvements
                </h3>
                
                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-300 mb-3 font-medium text-base">
                    {regenerationProgress.message || 'AI is regenerating your campaign...'}
                  </p>
                  
                  <div className="w-full">
                    {/* Progress Stats */}
                    <div className="flex justify-between text-sm mb-3 text-gray-500 dark:text-gray-400">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        Applying {consolidatedSuggestions.length} AI improvements
                      </span>
                      {regenerationProgress.startTime && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          colorTheme === 'purple' 
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' 
                            : colorTheme === 'blue' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                            : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                        }`}>
                          {Math.floor((Date.now() - regenerationProgress.startTime) / 1000)}s
                        </span>
                      )}
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4 shadow-inner">
                      <div 
                        className={`h-4 rounded-full transition-all duration-1000 ease-out shadow-lg flex items-center justify-end pr-3 ${
                          colorTheme === 'purple' 
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600' 
                            : colorTheme === 'blue' 
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600' 
                            : 'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${regenerationProgress.actualProgress || 5}%` }}
                      >
                        {regenerationProgress.actualProgress > 0 && (
                          <span className="text-white text-xs font-bold">
                            {regenerationProgress.actualProgress}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Dashboard Button */}
                <button
                  onClick={() => {
                    console.log('🚀 AI Feedback Regenerate: User clicked "Go to Dashboard"');
                    window.userChoseBackgroundMode = true;
                    setIsRegenerating(false);
                    if (onClose) {
                      onClose();
                    }
                    // Navigate to dashboard if in main app context
                    if (window.location.pathname !== '/dashboard') {
                      window.location.hash = '#dashboard';
                    }
                  }}
                  className={`w-full font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-white ${
                    colorTheme === 'purple' 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : colorTheme === 'blue' 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Continue in Background & Go to Dashboard
                </button>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Your campaign will continue regenerating. Check the dashboard or notifications for completion.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Custom Confirmation Modal */}
        <CustomConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleConfirmRegeneration}
          title="🚀 Regenerate Campaign with AI Improvements?"
          message={`This will create a NEW improved campaign based on AI recommendations.

✅ ${consolidatedSuggestions.length} improvements will be applied
✅ Original campaign will be preserved 
✅ You can compare both versions

💳 CREDIT COST BREAKDOWN:
${creditInfo.breakdown.map(item => `• ${item.type}: ${item.count} × ${item.costPerAd} = ${item.subtotal} credits`).join('\n')}

━━━━━━━━━━━━━━━━━━━━━━━━
📊 Total Cost: ${creditInfo.totalCredits} credits
💰 Your Balance: ${creditInfo.userBalance} credits
💵 After Regeneration: ${creditInfo.balanceAfter} credits
${creditInfo.balanceAfter < 0 ? '\n⚠️ Insufficient credits! Please purchase more credits.' : ''}`}
          confirmText={creditInfo.balanceAfter >= 0 ? `🎯 Regenerate (${creditInfo.totalCredits} credits)` : "❌ Insufficient Credits"}
          cancelText="Cancel"
          type={creditInfo.balanceAfter >= 0 ? "success" : "warning"}
        />

        {/* Success Celebration Modal */}
        {showSuccessCelebration && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl relative overflow-hidden">
              {/* Confetti animation background */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '1.5s' }}></div>
                <div className="absolute top-0 left-1/2 w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s', animationDuration: '1.8s' }}></div>
                <div className="absolute top-0 left-3/4 w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s', animationDuration: '1.6s' }}></div>
                <div className="absolute top-10 left-1/3 w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0.6s', animationDuration: '1.7s' }}></div>
                <div className="absolute top-10 right-1/4 w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.8s', animationDuration: '1.4s' }}></div>
              </div>
              
              <div className="text-center relative z-10">
                {/* Success Icon */}
                <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
                  colorTheme === 'purple' 
                    ? 'bg-purple-100 dark:bg-purple-900' 
                    : colorTheme === 'blue' 
                    ? 'bg-blue-100 dark:bg-blue-900' 
                    : 'bg-green-100 dark:bg-green-900'
                }`}>
                  <span className="text-5xl">🎉</span>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Campaign Successfully Regenerated!
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Your improved campaign is now ready with AI enhancements
                </p>
                
                {/* Success Details */}
                <div className={`rounded-xl p-4 mb-6 ${
                  colorTheme === 'purple' 
                    ? 'bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700' 
                    : colorTheme === 'blue' 
                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700' 
                    : 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700'
                }`}>
                  <div className="space-y-2 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">✅</span>
                      <span className="text-gray-700 dark:text-gray-300">Applied {consolidatedSuggestions.length} AI improvement suggestions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">✅</span>
                      <span className="text-gray-700 dark:text-gray-300">New enhanced campaign created</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500">✅</span>
                      <span className="text-gray-700 dark:text-gray-300">Original campaign preserved</span>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuccessCelebration(false);
                      if (onClose) onClose();
                      // Navigate to dashboard
                      if (window.location.pathname !== '/dashboard') {
                        window.location.hash = '#dashboard';
                      }
                    }}
                    className={`flex-1 py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                      colorTheme === 'purple' 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : colorTheme === 'blue' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    🏠 Go to Dashboard
                  </button>
                  <button
                    onClick={() => {
                      setShowSuccessCelebration(false);
                      if (onClose) onClose();
                      // Navigate to the new campaign
                      if (regeneratedCampaignId) {
                        window.location.hash = `#campaign/${regeneratedCampaignId}`;
                      }
                    }}
                    className="flex-1 py-3 px-6 rounded-lg font-semibold bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    👀 View New Campaign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AIFeedbackSimulation;